import { receiptProducts } from '../../receiptProducts';
import NumericInput from '../../components/NumericInput';
import { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import {
    Modal, ModalDialog, ModalClose,
    Box, Stack, Typography, Chip,
    Input, Select, Option, Button,
    IconButton, Textarea,
} from '@mui/joy';

import CreditCardRoundedIcon       from '@mui/icons-material/CreditCardRounded';
import PaymentsRoundedIcon         from '@mui/icons-material/PaymentsRounded';
import PointOfSaleRoundedIcon      from '@mui/icons-material/PointOfSaleRounded';
import AddRoundedIcon              from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon           from '@mui/icons-material/RemoveRounded';
import ReceiptLongRoundedIcon      from '@mui/icons-material/ReceiptLongRounded';
import DeleteOutlineRoundedIcon    from '@mui/icons-material/DeleteOutlineRounded';
import Inventory2RoundedIcon       from '@mui/icons-material/Inventory2Rounded';
import LocalOfferRoundedIcon       from '@mui/icons-material/LocalOfferRounded';
import PhoneIphoneRoundedIcon      from '@mui/icons-material/PhoneIphoneRounded';
import CategoryRoundedIcon         from '@mui/icons-material/CategoryRounded';
import ScheduleRoundedIcon         from '@mui/icons-material/ScheduleRounded';
import ShoppingCartRoundedIcon     from '@mui/icons-material/ShoppingCartRounded';

import { AppContext } from '../../context';
import {
    APIGetEmployees,
    APICreateOrder,
    APIGetDayAssignments,
    APIGetProducts,
    APIGetDiscounts,
    APIGetProductCategories,
    APIGetPaymentNumbers,
    APIWorkstationPhotographersStatus,
    APIWorkstationGetAdmins,
    parseApiError,
} from '../../api';

import DiscountPickerModal from './DiscountPickerModal';

const fmtMoney = (val) => {
    const n = parseFloat(val) || 0;
    return n.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
};

const MONEY_TYPES = [
    { v: 'transfer', label: 'Перевод',  icon: <CreditCardRoundedIcon  sx={{ fontSize: 16 }} />, c: '#818cf8' },
    { v: 'cash',     label: 'Наличные', icon: <PaymentsRoundedIcon    sx={{ fontSize: 16 }} />, c: '#22c55e' },
    { v: 'terminal', label: 'Терминал', icon: <PointOfSaleRoundedIcon sx={{ fontSize: 16 }} />, c: '#f59e0b' },
];

// «Сейчас» в формате datetime-local (input не понимает Z/секунды)
const nowLocal = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Считаем сумму скидки от базы
const calcDiscountAmount = (base, disc) => {
    if (!disc) return 0;
    const v = parseFloat(disc.value) || 0;
    return disc.is_percent ? base * v / 100 : v;
};


export default function WorkstationAddOrderModal({ open, onClose, onCreated, locationId, canSetDate = false }) {
    const { token, showToast } = useContext(AppContext);

    // ── Справочники / люди ────────────────────────────────────
    const [photographers, setPhotographers] = useState([]);
    const [loadingPh,     setLoadingPh]     = useState(false);
    const [products,       setProducts]       = useState([]);
    const [discounts,      setDiscounts]      = useState([]);
    const [categories,     setCategories]     = useState([]);
    const [paymentNumbers, setPaymentNumbers] = useState([]);
    const [admins,         setAdmins]         = useState([]);

    // ── Поля формы ────────────────────────────────────────────
    const [phId,            setPhId]            = useState('');
    const [items,           setItems]           = useState([]);
    const [orderDiscount,   setOrderDiscount]   = useState(null);
    const [comment,         setComment]         = useState('');
    const [receiptDt,       setReceiptDt]       = useState('');
    const [adminId,         setAdminId]         = useState(null);

    // Режим оплаты выбирает админ перед добавлением чека.
    const [paymentMode,      setPaymentMode]      = useState('single');         // 'single' | 'per_item'
    const [singleMoneyType,  setSingleMoneyType]  = useState('transfer');       // используется в single
    // Строки оплаты для «Единой оплаты»: делим оплату всего чека по способам
    // (как «Добавить строку» у фотографов). Позиции с этим не связаны.
    const [singleSplits,     setSingleSplits]     = useState([{ value: '', money_type: 'cash' }]);
    const [submitting,      setSubmitting]      = useState(false);

    const [categoryFilter,  setCategoryFilter]  = useState('all'); // 'all' | 'none' | <id>
    const [search,          setSearch]          = useState('');

    // ── Модалка выбора скидки ─────────────────────────────────
    const [discPicker, setDiscPicker] = useState({ open: false, target: 'item', itemIdx: null });

    // ── Загрузка справочников ─────────────────────────────────
    useEffect(() => {
        if (!open || !token) return;
        let cancelled = false;
        (async () => {
            setLoadingPh(true);

            // Фотографов берём через workstation-эндпоинт: он возвращает только
            // фотографов сегодняшней точки и доступен с правами workstation.
            // (Старый путь через APIGetDayAssignments требовал schedule.view, которого нет.)
            const [phStatusRes, prods, discs, cats, pNums, adminsRes] = await Promise.all([
                APIWorkstationPhotographersStatus(token),
                APIGetProducts(token, { is_active: true }),
                APIGetDiscounts(token, { is_active: true }),
                APIGetProductCategories(token, { is_active: true, with_counts: true }),
                APIGetPaymentNumbers(token, { is_active: true }),
                APIWorkstationGetAdmins(token),
            ]);

            if (cancelled) return;

            if (Array.isArray(prods)) setProducts(prods);
            if (Array.isArray(discs)) setDiscounts(discs);
            if (Array.isArray(cats))  setCategories(cats);
            if (Array.isArray(pNums)) {
                const filtered = pNums.filter(p => !p.location || p.location === locationId);
                setPaymentNumbers(filtered);
            }

            // Правка #4: админы сегодняшней смены — для пикера «Админ чека»
            const adminList = Array.isArray(adminsRes?.admins)
                ? adminsRes.admins
                : (Array.isArray(adminsRes) ? adminsRes : []);
            setAdmins(adminList);

            // Мапим в ту же форму, что использовалась раньше (id + full_name + first_name)
            const photographersFromStatus = Array.isArray(phStatusRes?.photographers)
                ? phStatusRes.photographers.map(p => ({
                    id:         p.photographer_id,
                    full_name:  p.photographer_name,
                    first_name: (p.photographer_name || '').split(' ')[0] || '',
                    last_name:  (p.photographer_name || '').split(' ').slice(1).join(' ') || '',
                    // На случай, если где-то ещё в файле проверяют:
                    is_blocked: false,
                    is_fired:   false,
                    // Доп. поля, могут пригодиться в будущем (например, для метки «у другого админа»)
                    _status:           p.status,                      // 'free' | 'busy'
                    _current_admin_id: p.current_admin_id || null,
                    _current_admin_name: p.current_admin_name || null,
                }))
                : [];

            setPhotographers(photographersFromStatus);
            setLoadingPh(false);
        })();
        return () => { cancelled = true; };
    }, [open, token, locationId]);

    // ── Сброс формы ───────────────────────────────────────────
    useEffect(() => {
        if (!open) {
            setPhId('');
            setItems([]);
            setOrderDiscount(null);
            setComment('');
            setReceiptDt('');
            setAdminId(null);
            setCategoryFilter('all');
            setSearch('');
            setSubmitting(false);
        }
    }, [open]);

    // ── Помощники: позиции ────────────────────────────────────
    const addItemByProduct = useCallback((product) => {
        setItems(prev => {
            const defaultMt = paymentMode === 'single' ? singleMoneyType : null;
            // Дедуп: позиция того же товара с тем же типом оплаты, той же ценой и
            // без скидки/комментария — +1 к количеству. Позиции из «Разделить
            // оплату» имеют изменённую цену/тип и не сливаются.
            const existsIdx = prev.findIndex(it =>
                it.product_id === product.id
                && it.money_type === defaultMt
                && !it.discount && !it.comment
                && (parseFloat(it.unit_price) || 0) === (parseFloat(product.price) || 0),
            );
            if (existsIdx !== -1) {
                return prev.map((it, i) =>
                    i === existsIdx ? { ...it, quantity: it.quantity + 1 } : it,
                );
            }
            return [...prev, {
                _uid: `${product.id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                product_id: product.id,
                product,
                quantity: 1,
                unit_price: parseFloat(product.price) || 0,
                money_type: defaultMt,   // тип оплаты выбирается явно пользователем
                comment: '',
                discount: null,
            }];
        });
    }, [paymentMode, singleMoneyType]);

    // Делит позицию на две строки, чтобы задать разные суммы и типы оплаты.
    const splitItem = (idx) => {
        setPaymentMode('per_item');   // деление имеет смысл только по позициям
        setItems(prev => {
            const it = prev[idx];
            const full = (parseFloat(it.unit_price) || 0) * (it.quantity || 1);
            const half = Math.round(full / 2);
            const base = {
                ...it, quantity: 1, discount: null,
            };
            const left  = { ...base, _uid: `${it.product_id}-${Date.now()}-a`, unit_price: full - half, money_type: null };
            const right = { ...base, _uid: `${it.product_id}-${Date.now()}-b`, unit_price: half,        money_type: null };
            const next = [...prev];
            next.splice(idx, 1, left, right);
            return next;
        });
    };

    const removeItem    = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));
    const updateItemQty = (idx, delta) => setItems(prev => prev.map((it, i) => {
        if (i !== idx) return it;
        const next = it.quantity + delta;
        return next > 0 ? { ...it, quantity: next } : it;
    }));
    const updateItemPrice = (idx, value) => setItems(prev => prev.map((it, i) => {
        if (i !== idx) return it;
        if (!it.product?.allow_price_edit) return it;   // цену меняем только у товаров с гибкой ценой (например «Фотосессия»)
        return { ...it, unit_price: value === "" ? "" : value };
    }));
    const setItemDiscount    = (idx, disc) => setItems(prev => prev.map((it, i) =>
        i === idx ? { ...it, discount: disc } : it,
    ));
    const setItemComment = (idx, val) => setItems(prev => prev.map((it, i) =>
        i === idx ? { ...it, comment: val.slice(0, 200) } : it,
    ));
    const setItemMoneyType = (idx, mt) => setItems(prev => prev.map((it, i) =>
        i === idx ? { ...it, money_type: mt } : it,
    ));

    // Строки оплаты «Единой оплаты» (как «Добавить строку» у фотографов)
    const addSplit    = () => setSingleSplits(prev => [...prev, { value: '', money_type: 'cash' }]);
    const removeSplit = (idx) => setSingleSplits(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
    const updateSplit = (idx, field, val) => setSingleSplits(prev => prev.map((l, i) =>
        i === idx ? { ...l, [field]: val } : l,
    ));

    // ── Подсчёт ───────────────────────────────────────────────
    const itemSubtotal   = (it) => it.unit_price * it.quantity;
    const itemTotal      = (it) => Math.max(0, itemSubtotal(it) - calcDiscountAmount(itemSubtotal(it), it.discount));
    const subtotalBeforeOrderDisc = useMemo(
        () => items.reduce((s, it) => s + itemTotal(it), 0),
        [items],
    );
    const orderDiscAmount = useMemo(
        () => calcDiscountAmount(subtotalBeforeOrderDisc, orderDiscount),
        [subtotalBeforeOrderDisc, orderDiscount],
    );
    const grandTotal = Math.max(0, subtotalBeforeOrderDisc - orderDiscAmount);

    // Сколько распределено по строкам оплаты (для «Единой оплаты»)
    const splitPaid = useMemo(
        () => singleSplits.reduce((s, l) => s + (parseFloat(l.value) || 0), 0),
        [singleSplits],
    );
    const splitMatches = Math.round(splitPaid) === Math.round(grandTotal);

    // Разбивка subtotal'а по типам оплаты — для отображения в итогах.
    // Позиции без выбранного типа оплаты в разбивку не идут.
    const moneyTypesBreakdown = useMemo(() => {
        const map = new Map();
        for (const it of items) {
            if (!it.money_type) continue;
            map.set(it.money_type, (map.get(it.money_type) || 0) + itemTotal(it));
        }
        return [...map.entries()].map(([mt, total]) => ({ mt, total }));
    }, [items]);

    // Есть ли в чеке хоть одна позиция с переводом — нужно для блока «Номер для оплаты».
    const hasTransfer = useMemo(
        () => items.some(it => it.money_type === 'transfer'),
        [items],
    );

    // ── Валидация ─────────────────────────────────────────────
    const validation = useMemo(() => {
        if (!phId)            return { ok: false, reason: 'Выберите фотографа' };
        if (items.length === 0) return { ok: false, reason: 'Добавьте хотя бы одну позицию' };
        // В per_item админ должен явно выбрать money_type у каждой позиции
        if (paymentMode === 'per_item' && items.some(it => !it.money_type)) {
            return { ok: false, reason: 'У каждой позиции выберите тип оплаты' };
        }
        // В single оплата разбита по строкам — их сумма должна сойтись с суммой чека
        if (paymentMode === 'single') {
            if (singleSplits.every(l => !parseFloat(l.value))) {
                return { ok: false, reason: 'Укажите оплату (хотя бы одну строку)' };
            }
            if (Math.round(splitPaid) !== Math.round(grandTotal)) {
                return { ok: false, reason: `Оплата ${splitPaid.toLocaleString('ru-RU')} ₽ ≠ сумме чека ${grandTotal.toLocaleString('ru-RU')} ₽` };
            }
        }
        return { ok: true };
    }, [phId, items, paymentMode, singleSplits, splitPaid, grandTotal]);

    // ── Список товаров для левой панели ───────────────────────
    const productsToShow = useMemo(() => {
        let arr = receiptProducts(products);
        if (categoryFilter === 'none') arr = arr.filter(p => !p.category);
        else if (categoryFilter !== 'all') arr = arr.filter(p => p.category === categoryFilter);
        const q = search.trim().toLowerCase();
        if (q) arr = arr.filter(p => p.name.toLowerCase().includes(q));
        return arr;
    }, [products, categoryFilter, search]);

    // ── Сабмит ────────────────────────────────────────────────
    const handleSubmit = async () => {
        if (!validation.ok) return;
        setSubmitting(true);

        // Новый плоский формат: у каждой позиции свой money_type.
        // Группировка по money_type и пропорциональное распределение
        // скидки на весь чек — на стороне сервера.
        const payload = {
            photographer_id: phId,
            location_id: locationId,
            administrator_id: adminId || null,
            payment_mode: paymentMode,
            items: items.map(it => {
                const obj = {
                    product_id: it.product_id,
                    quantity:   it.quantity,
                    unit_price: Number(it.unit_price || 0),
                    money_type: paymentMode === 'single' ? (singleSplits[0]?.money_type || 'cash') : it.money_type,
                    comment:    it.comment || '',
                };
                if (it.discount) {
                    obj.discount = {
                        discount_id: it.discount.discount_id || null,
                        name:        it.discount.name || '',
                        value:       parseFloat(it.discount.value) || 0,
                        is_percent:  !!it.discount.is_percent,
                    };
                }
                return obj;
            }),
            comment: comment.trim() || undefined,
        };
        if (orderDiscount) {
            payload.whole_check_discount = {
                discount_id: orderDiscount.discount_id || null,
                name:        orderDiscount.name || '',
                value:       parseFloat(orderDiscount.value) || 0,
                is_percent:  !!orderDiscount.is_percent,
            };
        }
        // «Единая оплата»: разбивка оплаты чека по способам (строки оплаты).
        // Сервер по ней создаст по одному чеку на способ (общий receipt_uid).
        if (paymentMode === 'single') {
            payload.payment_splits = singleSplits
                .filter(s => parseFloat(s.value) > 0)
                .map(s => ({ value: parseFloat(s.value), money_type: s.money_type }));
        }
        // receipt_datetime отправляем ТОЛЬКО при наличии права set_date
        if (canSetDate && receiptDt) {
            payload.receipt_datetime = new Date(receiptDt).toISOString();
        }

        const res = await APICreateOrder(token, payload);
        setSubmitting(false);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось добавить чек'), 'danger');
            return;
        }
        const total = Array.isArray(res)
            ? res.reduce((s, o) => s + parseFloat(o.value || 0), 0)
            : parseFloat(res?.value || 0);
        showToast(`Чек добавлен · ${total.toLocaleString('ru-RU')} ₽`);
        if (onCreated) onCreated();
        onClose();
    };

    // ── Открыть пикер скидки ──────────────────────────────────
    const openDiscPickerForItem  = (idx) => setDiscPicker({ open: true, target: 'item', itemIdx: idx });
    const openDiscPickerForOrder = ()    => setDiscPicker({ open: true, target: 'order', itemIdx: null });
    const closeDiscPicker = () => setDiscPicker({ open: false, target: 'item', itemIdx: null });
    const handleApplyDiscount = (disc) => {
        if (discPicker.target === 'order') setOrderDiscount(disc);
        else if (discPicker.itemIdx !== null) setItemDiscount(discPicker.itemIdx, disc);
    };

    // ── Категории + «Все»/«Без категории» для фильтра слева ──
    const categoryChips = useMemo(() => ([
        { id: 'all',  label: 'Все',           color: 'var(--joy-palette-primary-500)' },
        ...categories.filter(c => c.is_active).map(c => ({
            id: c.id, label: c.name, color: c.color || 'var(--joy-palette-primary-400)',
        })),
        { id: 'none', label: 'Без категории', color: 'var(--joy-palette-text-tertiary)' },
    ]), [categories]);

    const categoryById = useMemo(
        () => Object.fromEntries(categories.map(c => [c.id, c])),
        [categories],
    );

    return (
        <>
            <Modal
                open={open}
                onClose={() => !submitting && onClose()}
                slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', bgcolor: 'rgba(0,0,0,0.65)' } } }}
            >
                <ModalDialog sx={{
                    bgcolor: 'var(--joy-palette-background-level1)',
                    border: '1px solid var(--joy-palette-focusVisible)',
                    borderRadius: 'var(--joy-radius-xl)',
                    width: { xs: 'calc(100vw - 16px)', md: 'min(1180px, calc(100vw - 48px))' },
                    maxWidth: { xs: 'calc(100vw - 16px)', md: 1180 },
                    maxHeight: '95vh', gap: 0,
                    p: 0, overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                }}>
                    {/* Шапка модалки */}
                    <Box sx={{
                        px: { xs: 2, sm: 3 }, py: 1.5,
                        borderBottom: '1px solid var(--joy-palette-focusVisible)',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        bgcolor: 'var(--joy-palette-background-surface)',
                    }}>
                        <Typography sx={{ fontSize: 18, fontWeight: 800 }}>
                            <ReceiptLongRoundedIcon sx={{ fontSize: 20, verticalAlign: 'middle', mr: 0.5, color: 'var(--joy-palette-primary-400)' }} />
                            Добавить чек
                        </Typography>
                        <ModalClose sx={{ position: 'static' }} />
                    </Box>

                    {/* Тело: две колонки на ПК, стек на мобиле */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        flex: 1, minHeight: 0,
                    }}>
                        {/* ─────────── ЛЕВАЯ КОЛОНКА: терминал ─────────── */}
                        <Box sx={{
                            flex: { md: '1 1 58%' },
                            borderRight:  { md: '1px solid var(--joy-palette-focusVisible)' },
                            borderBottom: { xs: '1px solid var(--joy-palette-divider)', md: 'none' },
                            p: { xs: 1.5, sm: 2 },
                            overflowY: 'auto',
                            maxHeight: { xs: '50vh', md: 'calc(95vh - 60px)' },
                        }}>
                            {/* Поиск */}
                            <Input
                                placeholder="Поиск по товарам…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                size="sm"
                                sx={{ mb: 1.5 }}
                            />

                            {/* Чипы категорий */}
                            <Typography sx={sectionLabelSx}>
                                <CategoryRoundedIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.3 }} />
                                Категории
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                                {categoryChips.map(c => {
                                    const active = categoryFilter === c.id;
                                    return (
                                        <Box key={c.id}
                                            onClick={() => setCategoryFilter(c.id)}
                                            sx={{
                                                px: 1.2, py: 0.5, fontSize: 12, fontWeight: 700,
                                                borderRadius: 999, cursor: 'pointer',
                                                bgcolor: active ? c.color : 'var(--joy-palette-background-level2)',
                                                color:   active ? '#fff'  : 'var(--joy-palette-text-secondary)',
                                                border: `1.5px solid ${active ? c.color : 'var(--joy-palette-divider)'}`,
                                                transition: 'all .15s',
                                                '&:hover': { borderColor: c.color },
                                            }}>
                                            {c.label}
                                        </Box>
                                    );
                                })}
                            </Box>

                            {/* Сетка товаров */}
                            <Typography sx={sectionLabelSx}>
                                <Inventory2RoundedIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.3 }} />
                                Товары / услуги
                            </Typography>
                            {productsToShow.length === 0 ? (
                                <Box sx={{ py: 5, textAlign: 'center', color: 'var(--joy-palette-text-tertiary)', fontSize: 13 }}>
                                    Ничего не найдено
                                </Box>
                            ) : (
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
                                    gap: 1,
                                }}>
                                    {productsToShow.map(p => {
                                        const cat = p.category ? categoryById[p.category] : null;
                                        const catColor = cat?.color || null;
                                        return (
                                            <Box key={p.id}
                                                onClick={() => addItemByProduct(p)}
                                                sx={{
                                                    position: 'relative',
                                                    p: 1, cursor: 'pointer',
                                                    borderRadius: 'var(--joy-radius-md)',
                                                    border: `1px solid ${catColor || 'var(--joy-palette-divider)'}`,
                                                    borderLeftWidth: catColor ? 4 : 1,
                                                    bgcolor: 'var(--joy-palette-background-level2)',
                                                    transition: 'all .12s',
                                                    display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                                                    minHeight: 64,
                                                    '&:hover': {
                                                        borderColor: 'var(--joy-palette-primary-500)',
                                                        bgcolor: 'rgba(162,38,226,0.08)',
                                                        transform: 'translateY(-1px)',
                                                        boxShadow: '0 4px 12px rgba(162,38,226,0.18)',
                                                    },
                                                    '&:active': { transform: 'scale(0.97)' },
                                                }}>
                                                <Typography sx={{
                                                    fontSize: 12, fontWeight: 700,
                                                    color: 'var(--joy-palette-text-primary)',
                                                    lineHeight: 1.2,
                                                }}>
                                                    {p.name}
                                                </Typography>
                                                <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'var(--joy-palette-primary-300)', mt: 0.4 }}>
                                                    {Number(p.price).toLocaleString('ru-RU')} ₽
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                        </Box>

                        {/* ─────────── ПРАВАЯ КОЛОНКА: параметры + позиции ─────────── */}
                        <Box sx={{
                            flex: { md: '1 1 42%' },
                            p: { xs: 1.5, sm: 2 },
                            overflowY: 'auto',
                            maxHeight: { xs: 'none', md: 'calc(95vh - 60px)' },
                            display: 'flex', flexDirection: 'column', gap: 1.5,
                        }}>
                            {/* Фотограф */}
                            <Box>
                                <Typography sx={sectionLabelSx}>
                                    Фотограф <Box component="span" sx={{ color: '#f87171' }}>*</Box>
                                </Typography>
                                <Select
                                    size="sm"
                                    value={phId}
                                    onChange={(_, v) => setPhId(v)}
                                    placeholder={loadingPh ? 'Загрузка…' : 'Выберите фотографа'}
                                >
                                    {photographers.map(p => (
                                        <Option key={p.id} value={p.id}>{p.full_name || p.first_name}</Option>
                                    ))}
                                </Select>
                            </Box>

                            {/* Админ чека (правка #4) — на кого записать чек */}
                            {admins.length > 0 && (
                                <Box>
                                    <Typography sx={sectionLabelSx}>Админ чека</Typography>
                                    <Select
                                        size="sm"
                                        value={adminId}
                                        onChange={(_, v) => setAdminId(v)}
                                    >
                                        <Option value={null}>Авто (по фотографу)</Option>
                                        {admins.map(a => (
                                            <Option key={a.id} value={a.id}>
                                                {(a.full_name || `${a.first_name || ''} ${a.last_name || ''}`).trim()}
                                            </Option>
                                        ))}
                                    </Select>
                                    <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.3 }}>
                                        «Авто» — чек запишется на текущего админа фотографа. Можно выбрать другого.
                                    </Typography>
                                </Box>
                            )}

                            {/* Тумблер: единая / по позициям */}
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 0.6,
                                mb: 1, p: 0.4,
                                borderRadius: 'var(--joy-radius-md)',
                                bgcolor: 'var(--joy-palette-background-level2)',
                                border: '1px solid var(--joy-palette-divider)',
                            }}>
                                {[
                                    { v: 'single',   label: 'Единая оплата' },
                                    { v: 'per_item', label: 'По позициям' },
                                ].map(opt => {
                                    const active = paymentMode === opt.v;
                                    return (
                                        <Box key={opt.v}
                                            onClick={() => {
                                                setPaymentMode(opt.v);
                                                // При переключении в single — массово проставим текущий single-тип всем позициям
                                                if (opt.v === 'single') {
                                                    setItems(prev => prev.map(it => ({ ...it, money_type: singleMoneyType })));
                                                }
                                            }}
                                            sx={{
                                                flex: 1, py: 0.5, textAlign: 'center', cursor: 'pointer',
                                                fontSize: 12, fontWeight: 700,
                                                borderRadius: 'var(--joy-radius-sm)',
                                                bgcolor: active ? 'var(--joy-palette-primary-500)' : 'transparent',
                                                color:   active ? '#fff' : 'var(--joy-palette-text-secondary)',
                                                transition: 'all .15s',
                                            }}>
                                            {opt.label}
                                        </Box>
                                    );
                                })}
                            </Box>

                            {/* В single — строки оплаты (как «Добавить строку» у фотографов):
                                делим оплату всего чека по способам, позиции — отдельно. */}
                            {paymentMode === 'single' && (
                                <Box sx={{ mb: 1 }}>
                                    {singleSplits.map((line, idx) => (
                                        <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 0.6 }}>
                                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                                <NumericInput
                                                    size="sm" type="number" inputMode="decimal"
                                                    placeholder="Сумма"
                                                    value={line.value}
                                                    onChange={(e) => updateSplit(idx, 'value', e.target.value)}
                                                    sx={{ flex: 1, fontSize: 12 }}
                                                    endDecorator={<Typography sx={{ fontSize: 10 }}>₽</Typography>}
                                                />
                                                {singleSplits.length > 1 && (
                                                    <IconButton size="sm" variant="plain" color="danger"
                                                        onClick={() => removeSplit(idx)}>
                                                        <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                )}
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                {MONEY_TYPES.map(t => {
                                                    const active = line.money_type === t.v;
                                                    return (
                                                        <Box key={t.v}
                                                            onClick={() => updateSplit(idx, 'money_type', t.v)}
                                                            sx={{
                                                                flex: 1, py: 0.5, cursor: 'pointer',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.4,
                                                                borderRadius: 'var(--joy-radius-sm)',
                                                                bgcolor: active ? t.c : 'transparent',
                                                                color:   active ? '#fff' : t.c,
                                                                border: `1.5px solid ${active ? t.c : `${t.c}40`}`,
                                                                fontSize: 11, fontWeight: 700,
                                                                transition: 'all .12s',
                                                                '&:hover': { borderColor: t.c, bgcolor: active ? t.c : `${t.c}14` },
                                                                '& .MuiSvgIcon-root': { color: active ? '#fff' : t.c },
                                                            }}>
                                                            {t.icon}
                                                            <Box component="span">{t.label}</Box>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                    ))}

                                    {/* Добавить строку */}
                                    <Box
                                        onClick={addSplit}
                                        sx={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.4,
                                            py: 0.6, mt: 0.2, cursor: 'pointer',
                                            borderRadius: 'var(--joy-radius-sm)',
                                            border: '1.5px dashed var(--joy-palette-primary-400)',
                                            color: 'var(--joy-palette-primary-400)',
                                            fontSize: 12, fontWeight: 700,
                                            '&:hover': { bgcolor: 'rgba(162,38,226,0.08)' },
                                        }}>
                                        + Добавить строку
                                    </Box>

                                    {/* Индикатор: распределено vs сумма чека */}
                                    <Typography sx={{
                                        mt: 0.6, fontSize: 11, fontWeight: 700, textAlign: 'center',
                                        color: splitMatches ? 'var(--joy-palette-success-400)' : '#f87171',
                                    }}>
                                        Оплата: {splitPaid.toLocaleString('ru-RU')} ₽ / чек {grandTotal.toLocaleString('ru-RU')} ₽
                                        {!splitMatches && ' — суммы должны совпасть'}
                                    </Typography>
                                </Box>
                            )}

                            {/* Позиции */}
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.6 }}>
                                    <Typography sx={sectionLabelSx}>
                                        <ShoppingCartRoundedIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.3 }} />
                                        Позиции
                                    </Typography>
                                    <Chip size="sm" sx={{ bgcolor: 'rgba(162,38,226,0.15)', color: 'var(--joy-palette-primary-300)', fontWeight: 700 }}>
                                        {items.length}
                                    </Chip>
                                </Box>

                                {items.length === 0 ? (
                                    <Box sx={{
                                        p: 2, textAlign: 'center',
                                        border: '1.5px dashed var(--joy-palette-divider)',
                                        borderRadius: 'var(--joy-radius-md)',
                                        color: 'var(--joy-palette-text-tertiary)', fontSize: 12,
                                    }}>
                                        Нажми на товар слева, чтобы добавить позицию
                                    </Box>
                                ) : (
                                    <Stack spacing={0.5}>
                                        {items.map((it, idx) => {
                                            const sub = itemSubtotal(it);
                                            const disc = calcDiscountAmount(sub, it.discount);
                                            const total = Math.max(0, sub - disc);
                                            return (
                                                <Box key={it._uid} sx={{
                                                    p: 0.6,
                                                    border: '1px solid var(--joy-palette-divider)',
                                                    borderRadius: 'var(--joy-radius-md)',
                                                    bgcolor: 'var(--joy-palette-background-level2)',
                                                }}>
                                                    {/* Шапка: название + цена + удалить */}
                                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                                {it.product.name}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.2 }}>
                                                                <NumericInput
                                                                    size="sm" type="number" inputMode="decimal"
                                                                    value={it.unit_price}
                                                                    onChange={(e) => updateItemPrice(idx, e.target.value)}
                                                                    readOnly={!it.product?.allow_price_edit}
                                                                    sx={{ width: 90, fontSize: 12,
                                                                        opacity: it.product?.allow_price_edit ? 1 : 0.7 }}
                                                                    endDecorator={<Typography sx={{ fontSize: 10 }}>₽</Typography>}
                                                                />
                                                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                                                    × {it.quantity} = <b style={{ color: 'var(--joy-palette-primary-300)' }}>{total.toLocaleString('ru-RU')} ₽</b>
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                        <IconButton size="sm" variant="plain" color="danger"
                                                            onClick={() => removeItem(idx)}>
                                                            <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                        <Button size="sm" variant="plain"
                                                            onClick={() => splitItem(idx)}
                                                            sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-primary-300)', minHeight: 0, px: 0.8 }}
                                                        >
                                                            Разделить оплату
                                                        </Button>
                                                    </Box>

                                                    {/* Тип оплаты для этой позиции — только в per_item режиме */}
                                                    {paymentMode === 'per_item' && (
                                                        <Box sx={{ mt: 0.6 }}>
                                                            {!it.money_type && (
                                                                <Typography sx={{ fontSize: 10, color: '#ef4444', fontWeight: 700, mb: 0.3 }}>
                                                                    Выберите тип оплаты
                                                                </Typography>
                                                            )}
                                                            <Stack direction="row" spacing={0.3}>
                                                                {MONEY_TYPES.map(t => {
                                                                    const active = it.money_type === t.v;
                                                                    const needsAttention = !it.money_type;
                                                                    return (
                                                                        <Box key={t.v}
                                                                            onClick={(e) => { e.stopPropagation(); setItemMoneyType(idx, t.v); }}
                                                                            sx={{
                                                                                py: 0.5, px: 2,
                                                                                cursor: 'pointer',
                                                                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3,
                                                                                borderRadius: 'lg',
                                                                                bgcolor: active ? t.c : 'transparent',
                                                                                color:   active ? '#fff' : t.c,
                                                                                border: `1px solid ${active ? t.c : (needsAttention ? '#ef4444' : `${t.c}40`)}`,
                                                                                fontSize: 13, fontWeight: 700,
                                                                                transition: 'all .12s',
                                                                                '&:hover': { borderColor: t.c, bgcolor: active ? t.c : `${t.c}18` },
                                                                                '& .MuiSvgIcon-root': { fontSize: 12, color: active ? '#fff' : t.c },
                                                                            }}>
                                                                                {t.icon}
                                                                            <Box component="span">{t.label}</Box>
                                                                        </Box>
                                                                    );
                                                                })}
                                                            </Stack>
                                                        </Box>
                                                    )}

                                                    {/* Скидка позиции */}
                                                    {it.discount ? (
                                                        <Box sx={{
                                                            mt: 0.8, p: 0.8,
                                                            bgcolor: 'rgba(245,158,11,0.10)',
                                                            border: '1px solid rgba(245,158,11,0.3)',
                                                            borderRadius: 'var(--joy-radius-sm)',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                        }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <LocalOfferRoundedIcon sx={{ fontSize: 13, color: '#f59e0b' }} />
                                                                <Typography sx={{ fontSize: 11, color: '#f59e0b', fontWeight: 700 }}>
                                                                    {it.discount.name || 'Скидка'} · −{it.discount.value}{it.discount.is_percent ? '%' : '₽'}
                                                                </Typography>
                                                            </Box>
                                                            <IconButton size="sm" variant="plain" color="danger"
                                                                onClick={() => setItemDiscount(idx, null)}>
                                                                <DeleteOutlineRoundedIcon sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                        </Box>
                                                    ) : (
                                                        <Button
                                                            size="sm" variant="plain"
                                                            startDecorator={<LocalOfferRoundedIcon sx={{ fontSize: 14 }} />}
                                                            onClick={() => openDiscPickerForItem(idx)}
                                                            sx={{ mt: 0.5, fontSize: 11, color: '#f59e0b', minHeight: 24 }}>
                                                            Скидка
                                                        </Button>
                                                    )}

                                                    {/* Кол-во: −  N  + */}
                                                    <Box sx={{
                                                        mt: 0.5,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5,
                                                    }}>
                                                        <IconButton size="sm" variant="outlined" color="neutral"
                                                            onClick={() => updateItemQty(idx, -1)}
                                                            disabled={it.quantity <= 1}
                                                            sx={{ width: 28, height: 28, borderRadius: 'var(--joy-radius-sm)' }}>
                                                            <RemoveRoundedIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                        <Typography sx={{
                                                            minWidth: 28, textAlign: 'center',
                                                            fontSize: 14, fontWeight: 800,
                                                            color: 'var(--joy-palette-text-primary)',
                                                        }}>
                                                            {it.quantity}
                                                        </Typography>
                                                        <IconButton size="sm" variant="outlined" color="primary"
                                                            onClick={() => updateItemQty(idx, 1)}
                                                            sx={{ width: 28, height: 28, borderRadius: 'var(--joy-radius-sm)' }}>
                                                            <AddRoundedIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Stack>
                                )}
                            </Box>

                            {/* Скидка на чек */}
                            {items.length > 0 && (
                                <Box>
                                    <Typography sx={sectionLabelSx}>
                                        <LocalOfferRoundedIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.3 }} />
                                        Скидка на весь чек
                                    </Typography>
                                    {orderDiscount ? (
                                        <Box sx={{
                                            p: 1,
                                            bgcolor: 'rgba(245,158,11,0.10)',
                                            border: '1px solid rgba(245,158,11,0.3)',
                                            borderRadius: 'var(--joy-radius-md)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        }}>
                                            <Typography sx={{ fontSize: 12, color: '#f59e0b', fontWeight: 700 }}>
                                                {orderDiscount.name} · −{orderDiscount.value}{orderDiscount.is_percent ? '%' : '₽'}
                                                <Box component="span" sx={{ ml: 0.5, color: 'var(--joy-palette-text-tertiary)' }}>
                                                    (−{orderDiscAmount.toLocaleString('ru-RU')} ₽)
                                                </Box>
                                            </Typography>
                                            <IconButton size="sm" variant="plain" color="danger"
                                                onClick={() => setOrderDiscount(null)}>
                                                <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <Button
                                            size="sm" variant="outlined"
                                            startDecorator={<AddRoundedIcon sx={{ fontSize: 14 }} />}
                                            onClick={openDiscPickerForOrder}
                                            sx={{ color: '#f59e0b', borderColor: 'rgba(245,158,11,0.3)' }}>
                                            Добавить скидку на чек
                                        </Button>
                                    )}
                                </Box>
                            )}

                            {/* Комментарий */}
                            <Box>
                                <Typography sx={sectionLabelSx}>Комментарий</Typography>
                                <Textarea
                                    size="sm" minRows={2} maxRows={3}
                                    placeholder="Имя клиента, флешка, кадры…"
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value.slice(0, 200))}
                                    sx={{ color: 'white' }}
                                />
                            </Box>

                            {/* Дата и время — только тем, у кого есть finance.orders.set_date */}
                            {canSetDate && (
                                <Box>
                                    <Typography sx={sectionLabelSx}>
                                        <ScheduleRoundedIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.3 }} />
                                        Дата и время чека
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                        <Input
                                            size="sm" type="datetime-local"
                                            value={receiptDt}
                                            onChange={(e) => setReceiptDt(e.target.value)}
                                            placeholder={nowLocal()}
                                            sx={{ flex: 1, color: 'white' }}
                                        />
                                        {receiptDt && (
                                            <Button size="sm" variant="outlined" color="neutral"
                                                onClick={() => setReceiptDt('')}>
                                                Сейчас
                                            </Button>
                                        )}
                                    </Box>
                                    <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.3 }}>
                                        {receiptDt
                                            ? `Будет оформлен на ${new Date(receiptDt).toLocaleString('ru-RU')}`
                                            : 'По умолчанию — текущий момент. Поменяй для бэкдейта чека.'}
                                    </Typography>
                                </Box>
                            )}

                            {/* Итог */}
                            <Box sx={{
                                mt: 'auto',
                                p: 1.5,
                                bgcolor: 'rgba(34,197,94,0.10)',
                                border: '1px solid rgba(34,197,94,0.35)',
                                borderRadius: 'var(--joy-radius-md)',
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Итого:</Typography>
                                    <Typography sx={{ fontSize: 22, fontWeight: 900, color: '#22c55e' }}>
                                        {grandTotal.toLocaleString('ru-RU')} ₽
                                    </Typography>
                                </Box>
                                {orderDiscAmount > 0 && (
                                    <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', textAlign: 'right' }}>
                                        включая скидку на чек −{orderDiscAmount.toLocaleString('ru-RU')} ₽
                                    </Typography>
                                )}
                                {/* Разбивка по типам оплаты — если в чеке больше одного типа */}
                                {moneyTypesBreakdown.length > 1 && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mt: 0.6, justifyContent: 'flex-end' }}>
                                        {moneyTypesBreakdown.map(({ mt, total }) => {
                                            const meta = MONEY_TYPES.find(m => m.v === mt);
                                            if (!meta) return null;
                                            return (
                                                <Chip key={mt} size="sm" startDecorator={meta.icon}
                                                    sx={{ bgcolor: `${meta.c}20`, color: meta.c, fontSize: 10, fontWeight: 700 }}>
                                                    {total.toLocaleString('ru-RU')} ₽
                                                </Chip>
                                            );
                                        })}
                                    </Box>
                                )}
                            </Box>

                            {/* Кнопки */}
                            <Stack direction="row" spacing={1}>
                                <Button variant="soft" color="danger" onClick={onClose}
                                    sx={{ flex: 0 }}>
                                    Отмена
                                </Button>
                                <Button
                                    fullWidth
                                    loading={submitting}
                                    disabled={!validation.ok}
                                    onClick={handleSubmit}
                                    startDecorator={<AddRoundedIcon />}
                                    sx={{
                                        flex: 1,
                                        background: validation.ok
                                            ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                                            : 'var(--joy-palette-background-level2)',
                                        color: '#fff', fontWeight: 800,
                                        opacity: validation.ok ? 1 : 0.5,
                                    }}>
                                    {validation.ok
                                        ? `Добавить чек на ${grandTotal.toLocaleString('ru-RU')} ₽`
                                        : (validation.reason || 'Заполните форму')}
                                </Button>
                            </Stack>
                        </Box>
                    </Box>
                </ModalDialog>
            </Modal>

            {/* Пикер скидки */}
            <DiscountPickerModal
                open={discPicker.open}
                onClose={closeDiscPicker}
                onApply={handleApplyDiscount}
                discounts={discounts}
                categories={categories}
                product={
                    discPicker.target === 'item' && discPicker.itemIdx !== null
                        ? items[discPicker.itemIdx]?.product || null
                        : null
                }
                target={discPicker.target}
            />
        </>
    );
}

const sectionLabelSx = {
    fontSize: 10, fontWeight: 700,
    color: 'var(--joy-palette-text-tertiary)',
    textTransform: 'uppercase', letterSpacing: '0.05em',
    mb: 0.4,
};
const selectListSx = {
    bgcolor: 'var(--joy-palette-background-level2)',
    border: '1px solid var(--joy-palette-divider)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
    '& .MuiOption-root': {
        color: 'var(--joy-palette-text-primary)',
        fontSize: 13,
        '&:hover': { bgcolor: 'rgba(162,38,226,0.12)', color: 'var(--joy-palette-text-primary)' },
        '&[aria-selected="true"]': {
            bgcolor: 'rgba(162,38,226,0.2)',
            color: 'var(--joy-palette-primary-300)',
            fontWeight: 600,
        },
    },
};